import { noop } from "frontend/utils/fn-utils";
import Konva from "konva";
import { useRef } from "react";
import { Group, Rect } from "react-konva";
import consts from "shared/consts";
import { CARD_HEIGHT, CARD_WIDTH, GAP_Y } from "shared/datamodel/card-stack";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import { IntegrationType } from "shared/integrations/integration";
import { MondayItemForLiveCardStack, MondayColumn } from "shared/integrations/mapper";
import BaseCanvasElement from "../base-canvas-element";
import { MondayItemInLiveCardStack } from "../integration-element/integration-element";
import { clamp, viewportToStage } from "utils/math-utils";

export interface BoardInfo {
  integrationId: string;
  boardId: string;
  name: string;
  columns: MondayColumn[];
  columnsToDisplay: Set<string>;
}

const DISTANCE_BETWEEN_CARDS = GAP_Y + CARD_HEIGHT;

export function LiveCardStackContents({
  documentId,
  board,
  subBoard,
  allItems,
  numItemsToDisplay,
  showSubItems,
  calcPosition,
  changeElement,
  onDragEnd,
  cardLayoutStartY,
}: {
  documentId: string;
  board: BoardInfo;
  subBoard?: BoardInfo;
  allItems: MondayItemForLiveCardStack[];
  numItemsToDisplay: number;
  showSubItems: boolean;
  calcPosition: (cardIndex: number) => { x: number; y: number };
  changeElement: (
    props: any,
    undoConfig: { shouldAdd: boolean; previousProps?: any },
    updateSubMenuData?: any, //take the last change and make it the default when selecting this element again from the menu (e.g. make the default color the last color selected)
    elementId?: string
  ) => void;
  onDragEnd: (e: any, b: BoardInfo, selectedColumns: any[], itemId: string) => void;
  cardLayoutStartY: number;
}) {
  const placeholderRef = useRef<Konva.Rect>(null);
  let defaultSubBoardColumns: MondayColumn[] = [];
  if (subBoard) {
    defaultSubBoardColumns = subBoard.columns
      .filter((col: any) => col.type === "status" || col.type === "date")
      .slice(0, 2);
  }

  let cards = [];
  let itemIndex = 0;
  while (cards.length < numItemsToDisplay) {
    const item = allItems[itemIndex++];
    if (!item) {
      // I've seen undefined in the allItems array.. not sure why
      break;
    }
    const position = calcPosition(cards.length);
    const itemId = item.id;

    const { item: integrationElement, id: newId } = placeIntegrationElement(
      position,
      board.integrationId,
      IntegrationType.monday,
      {
        itemId,
      }
    );
    const id = `${consts.CANVAS_ELEMENTS.INTEGRATION}-${newId}`;

    const selectedColumns = item.columnValues.filter(
      ({ type, id }: any) => type !== "subtasks" && board.columnsToDisplay.has(id)
    );

    cards.push(
      <BaseCanvasElement
        key={id}
        id={id}
        type={"integrationItem"}
        x={position.x}
        y={position.y}
        element={integrationElement}
        onResize={noop}
        isSelectable={true}
        isEditingLink={false}
        isConnectable={false}
        isDraggable={false} // canvas-stage can't drag this one
        draggable={true} // konva drag will work
        onDragStart={(e: any) => {
          placeholderRef.current?.visible(true);
          placeholderRef.current?.position(e.target.position());
          e.target.attrs.startPosition = { ...e.target.position() };
          e.target.moveToTop(); // this makes the dragged element last sibling, and so on top of others
        }}
        onDragMove={(e: any) => {
          // this section copied from onDragEnd in parent.
          // should refactor so this calculation is always done in this level, and parent gets result
          const stage: Konva.Stage = e.target.getStage();
          const stageParams = { x: stage.x(), y: stage.y(), scale: stage.scaleX() };
          const mousePosition = viewportToStage(stageParams, { x: e.evt.offsetX, y: e.evt.offsetY });
          const children = [...e.target.getParent().getChildren()];
          const targetYPosition = mousePosition.y - cardLayoutStartY;
          const targetPlace = clamp(Math.floor(targetYPosition / DISTANCE_BETWEEN_CARDS), 0, numItemsToDisplay - 1);

          placeholderRef.current?.y(targetPlace * DISTANCE_BETWEEN_CARDS);
          let y = 0;
          // reposition the children to make way for the placeholder rectangle
          // notice we ignore the last child, because that is the element being dragged (e.target.moveToTop did that)
          for (let i = 0; i < children.length - 1; i++) {
            const child = children[i];
            if (i == targetPlace) {
              y += DISTANCE_BETWEEN_CARDS;
            }
            child.y(y);
            y += DISTANCE_BETWEEN_CARDS;
          }
        }}
        onDragEnd={(e: any) => {
          placeholderRef.current?.visible(false);
          onDragEnd(e, board, selectedColumns, itemId);
          // send the original card back to its position
          // must come after onDragEnd, since caller gets e.target.position (bad practice - change it)
          e.target.setPosition(e.target.attrs.startPosition);
        }}
        changeElement={changeElement}
        isDirty={true} // meaning it's not yet in reflect
      >
        <MondayItemInLiveCardStack
          id={id}
          integrationId={board.integrationId}
          documentId={documentId!}
          configuration={{ itemId }}
          allColumns={board.columns}
          selectedColumns={selectedColumns}
        />
      </BaseCanvasElement>
    );

    if (subBoard && showSubItems && item.subitems?.length) {
      for (const subitem of item.subitems) {
        if (cards.length >= numItemsToDisplay) {
          break;
        }
        const position = calcPosition(cards.length);
        const subitemId = subitem.id;

        const { item: elementSubitem, id: newId } = placeIntegrationElement(
          position,
          subBoard.integrationId,
          IntegrationType.monday,
          {
            itemId: subitemId,
          }
        );
        const id = `${consts.CANVAS_ELEMENTS.INTEGRATION}-${newId}`;

        const selectedColumns: any[] =
          subBoard.columnsToDisplay.size > 0
            ? subitem.columnValues.filter(
                ({ type, id }: any) => type !== "subtasks" && subBoard.columnsToDisplay.has(id)
              )
            : defaultSubBoardColumns;

        cards.push(
          <BaseCanvasElement
            key={id}
            id={id}
            type={"integrationItem"}
            x={position.x}
            y={position.y}
            element={elementSubitem}
            onResize={noop}
            isSelectable={true}
            isEditingLink={false}
            isConnectable={false}
            isDraggable={false} // canvas-stage can't drag this one
            draggable={true} // konva drag will work
            onDragStart={(e: any) => {
              placeholderRef.current?.visible(true);
              placeholderRef.current?.position(e.target.position());
              e.target.attrs.startPosition = { ...e.target.position() };
              e.target.moveToTop();
            }}
            onDragMove={(e: any) => {
              // For subitems: let the dragged card move freely, but constrain placeholder to parent's subitem area
              const children = [...e.target.getParent().getChildren()];
              const parentItemId = item.id;

              // Calculate the indices for this parent's subitems
              // TODO can be done once in onDragStart
              let parentSubitemStartIndex = -1;
              let parentSubitemEndIndex = -1;

              let currentIndex = 0;
              for (const item of allItems) {
                if (currentIndex >= numItemsToDisplay) break;

                // Skip the main item
                currentIndex++;

                if (item.subitems) {
                  if (item.id === parentItemId) {
                    parentSubitemStartIndex = currentIndex;
                    parentSubitemEndIndex = currentIndex + item.subitems.length - 1;
                    break;
                  }
                  currentIndex += item.subitems.length;
                }
              }

              if (parentSubitemStartIndex !== -1) {
                const stage: Konva.Stage = e.target.getStage();
                const stageParams = { x: stage.x(), y: stage.y(), scale: stage.scaleX() };
                const mousePosition = viewportToStage(stageParams, { x: e.evt.offsetX, y: e.evt.offsetY });
                const targetYPosition = mousePosition.y - cardLayoutStartY;
                // Constrain the placeholder calculation to the parent's subitem range
                const lastDisplayedChild = Math.min(parentSubitemEndIndex, numItemsToDisplay - 1);
                const targetPlace = clamp(
                  Math.floor(targetYPosition / DISTANCE_BETWEEN_CARDS),
                  parentSubitemStartIndex,
                  lastDisplayedChild
                );

                // Position the placeholder at the calculated index
                placeholderRef.current?.y(targetPlace * DISTANCE_BETWEEN_CARDS);

                let y = parentSubitemStartIndex * DISTANCE_BETWEEN_CARDS;
                // reposition the children to make way for the placeholder rectangle
                // notice we ignore the last child, because that is the element being dragged (e.target.moveToTop did that)
                for (let i = parentSubitemStartIndex; i < lastDisplayedChild; i++) {
                  const child = children[i];
                  if (i == targetPlace) {
                    y += DISTANCE_BETWEEN_CARDS;
                  }
                  child.y(y);
                  y += DISTANCE_BETWEEN_CARDS;
                }
              }
            }}
            onDragEnd={(e: any) => {
              placeholderRef.current?.visible(false);
              onDragEnd(e, subBoard, selectedColumns, subitemId);
              e.target.setPosition(e.target.attrs.startPosition); // send the original card back to its position
            }}
            changeElement={changeElement}
            isDirty={true} // meaning it's not yet in reflect
          >
            <MondayItemInLiveCardStack
              id={id}
              integrationId={subBoard.integrationId}
              documentId={documentId!}
              configuration={{ itemId: subitem.id }}
              // onChangeColumnValue={(columnId: string, value: any) =>
              // updateColumnValue(subBoard.integrationId, subitem.id, columnId, value)
              // }
              allColumns={subBoard.columns}
              selectedColumns={selectedColumns}
            />
          </BaseCanvasElement>
        );
      }
    }
  }

  return (
    <>
      <Rect
        visible={false}
        ref={placeholderRef}
        width={CARD_WIDTH}
        height={CARD_HEIGHT}
        fill="grey"
        opacity={0.1}
        strokeEnabled={false}
        listening={false}
      />
      <Group>{cards}</Group>
    </>
  );
}
